import { Button, Dialog, Stack, Typography } from '@mui/material';
import { FloorplanRevision } from '@thive/fddm-types';
import { ChangeEvent, memo, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { AppServiceFactory } from '@/modules/app/service/AppServiceFactory';
import { ProjectServiceConnector } from '@/modules/api/ProjectApiConnector';
import { buildRevisionFileStorageBasePath } from '@/modules/common/helpers/buildRevisionFileStorageBasePath';
import {
  FILENAME_FORBIDDEN_CHARACTERS,
  FLOORPLAN_IMPORT_ACCEPTABLE_TYPES,
  isObject,
  isZipFile,
} from '@/modules/common/helpers/file';
import { TextInput } from '@common/components/inputs';
import Label from '@/modules/common/components/Label';
import { useFileTypeValidator } from '@/modules/common/hooks/useFileValidator';
import { Theme } from '@/modules/common/types/general';
import { logger } from '@common/logging/logger';
import { useFloorplanObjectValidation } from '@/modules/floorplanV2/hooks/useFloorplanObjectValidation';
import {
  ParsedZipFile,
  useZipFileProcessor,
} from '@/modules/floorplanV2/hooks/useZipFileProcessor';
import { useSnackbarStore } from '@/modules/snackbar/store/useSnackbarStore';
import { UserPreferenceName } from '@modules/userPreferences';
import { useUserPreference } from '@modules/userPreferences/hooks';

import { useNavigation } from '../common/hooks';

type ParsedFloorplanImport = {
  revision: FloorplanRevision;
  zipFile?: ParsedZipFile;
};

const getImportedFloorplanName = (revision: FloorplanRevision): string => {
  const alternativeName = revision.metaData?.alternativeName;

  if (typeof alternativeName === 'string' && alternativeName.trim().length > 0) {
    return alternativeName.trim();
  }

  return '';
};

const FloorplanImporterComponent = () => {
  const { projectId } = useParams();
  const { t } = useTranslation(['interface', 'common', 'errors']);
  const { goToBuilder } = useNavigation();
  const { showSnackbar } = useSnackbarStore();
  const { validateFileType } = useFileTypeValidator();
  const { isValidFloorplan } = useFloorplanObjectValidation();
  const { processZipFile, readZipFile } = useZipFileProcessor();
  const theme = useUserPreference(UserPreferenceName.GENERAL_THEME);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isReadingFile, setIsReadingFile] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [parsedImport, setParsedImport] = useState<ParsedFloorplanImport | null>(null);
  const [selectedFileName, setSelectedFileName] = useState('');
  const [floorplanName, setFloorplanName] = useState('');

  const readFloorplanImport = useCallback(
    async (file: File): Promise<ParsedFloorplanImport | null> => {
      if (!validateFileType(file, FLOORPLAN_IMPORT_ACCEPTABLE_TYPES)) {
        return null;
      }

      if (isZipFile(file)) {
        const zipFile = await readZipFile(file);
        if (!zipFile?.floorplan) return null;

        return {
          revision: zipFile.floorplan,
          zipFile,
        };
      }

      try {
        const parsedRevision: unknown = JSON.parse(await file.text());
        if (!isObject(parsedRevision) || !isValidFloorplan(parsedRevision)) return null;

        return { revision: parsedRevision };
      } catch (error) {
        logger.error('Failed to parse imported floorplan:', error);
        return null;
      }
    },
    [isValidFloorplan, readZipFile, validateFileType],
  );

  const selectFile = useCallback(
    async (file: File) => {
      setIsReadingFile(true);

      try {
        const nextParsedImport = await readFloorplanImport(file);
        if (!nextParsedImport) {
          setParsedImport(null);
          setSelectedFileName('');
          setFloorplanName('');
          showSnackbar(t('errors:settings.incompatible_file'));
          return;
        }

        const importedFloorplanName = getImportedFloorplanName(nextParsedImport.revision);
        if (!importedFloorplanName) {
          setParsedImport(null);
          setSelectedFileName('');
          setFloorplanName('');
          logger.error('The imported floorplan does not contain a valid alternative name.');
          showSnackbar(t('errors:invalid_data_format'));
          return;
        }

        setParsedImport(nextParsedImport);
        setSelectedFileName(file.name);
        setFloorplanName(importedFloorplanName);
      } finally {
        setIsReadingFile(false);
      }
    },
    [readFloorplanImport, showSnackbar, t],
  );

  const importFloorplan = useCallback(async () => {
    if (!projectId) {
      logger.error('projectId was undefined.');
      return;
    }

    if (!parsedImport) return;

    const importedFloorplanName = floorplanName.trim();
    if (!importedFloorplanName) return;

    setIsImporting(true);

    try {
      const projectService = ProjectServiceConnector.getInstance();
      const alternative = await projectService.createLayoutAlternative({
        projectId,
        alternativeName: importedFloorplanName,
      });
      const initialRevision = await projectService.getFloorplanRevision({
        projectId,
        alternativeId: alternative.alternativeId,
        revisionId: alternative.lastRevisionId,
      });
      let revision = structuredClone(parsedImport.revision);
      revision.uxonly = revision.uxonly ?? structuredClone(initialRevision.uxonly);
      revision.uxonly.thumbnail = '';

      let savedMetaData = await projectService.saveFloorplanRevisionDraft({
        projectId,
        alternativeId: alternative.alternativeId,
        revision: {
          ...revision,
          metaData: initialRevision.metaData,
        },
      });

      if (parsedImport.zipFile) {
        const storageBasePath = buildRevisionFileStorageBasePath(savedMetaData);
        if (!storageBasePath) {
          throw new Error('Could not create the imported revision storage path.');
        }

        const processedRevision = await processZipFile(parsedImport.zipFile, storageBasePath);
        if (!processedRevision) throw new Error('Could not process the imported ZIP file.');

        revision = processedRevision;
        savedMetaData = await projectService.saveFloorplanRevisionDraft({
          projectId,
          alternativeId: alternative.alternativeId,
          revision: {
            ...revision,
            metaData: savedMetaData,
          },
        });
      }

      goToBuilder(alternative.alternativeId, savedMetaData.revisionId);
      AppServiceFactory.getInstance().createAppService().goToModule('canvas_setup');
    } catch (error) {
      logger.error('Failed to import floorplan:', error);
      showSnackbar(t('errors:home.create_floorplan'));
    } finally {
      setIsImporting(false);
    }
  }, [floorplanName, goToBuilder, parsedImport, processZipFile, projectId, showSnackbar, t]);

  const openDialog = useCallback(() => {
    setParsedImport(null);
    setSelectedFileName('');
    setFloorplanName('');
    setIsOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    if (isReadingFile || isImporting) return;

    setIsOpen(false);
  }, [isImporting, isReadingFile]);

  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.currentTarget.files?.[0];
      event.currentTarget.value = '';
      if (!file) return;

      selectFile(file);
    },
    [selectFile],
  );

  const handleFloorplanNameChange = useCallback((value: string) => {
    setFloorplanName(value);
  }, []);

  const isBusy = isReadingFile || isImporting;
  const importIsDisabled = isBusy || !parsedImport || floorplanName.trim().length === 0;

  return (
    <>
      <Button variant='outlined' onClick={openDialog} sx={{ py: 1, textTransform: 'none' }}>
        {t('interface:home.import_floorplan', 'Import floorplan')}
      </Button>

      <Dialog open={isOpen} onClose={closeDialog} className={theme === Theme.DARK ? 'dark' : ''}>
        <Stack gap={2} p={2} minWidth={400}>
          <Typography variant='h6' align='center'>
            {t('interface:home.import_floorplan', 'Import floorplan')}
          </Typography>

          <Stack gap={1}>
            <Button variant='outlined' onClick={openFilePicker} disabled={isBusy}>
              {isReadingFile
                ? t('interface:home.reading_floorplan_file', 'Reading file…')
                : t('interface:home.select_floorplan_file', 'Select file')}
            </Button>
            {selectedFileName && (
              <Typography variant='body2' color='text.secondary'>
                {selectedFileName}
              </Typography>
            )}
          </Stack>

          <Label label={t('interface:home.name', 'Name')}>
            <TextInput
              value={floorplanName}
              onChange={handleFloorplanNameChange}
              disabled={!parsedImport || isBusy}
              forbiddenCharacters={FILENAME_FORBIDDEN_CHARACTERS}
            />
          </Label>

          <Stack gap={1}>
            <Button variant='secondary' onClick={importFloorplan} disabled={importIsDisabled}>
              {isImporting
                ? t('interface:home.importing_floorplan', 'Importing floorplan…')
                : t('interface:home.import_floorplan', 'Import floorplan')}
            </Button>
            <Button onClick={closeDialog} disabled={isBusy}>
              {t('common:cancel', 'Cancel')}
            </Button>
          </Stack>
        </Stack>
      </Dialog>

      <input
        ref={fileInputRef}
        type='file'
        accept='.json,.zip,application/json,application/zip,application/x-zip-compressed'
        onChange={handleFileChange}
        hidden
      />
    </>
  );
};

export const FloorplanImporter = memo(FloorplanImporterComponent);
